import { getPartialVisibilityGutter, getWidthFromDeviceType } from "./elementWidth";

/**
 * Checks if there are not enough children to fill the slidesToShow.
 * @param {object} state - The internal carousel state.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @param {number} state.totalItems - The total number of items.
 * @returns {boolean} True if there are not enough children, false otherwise.
 */
function notEnoughChildren(state) {
  const {
    slidesToShow,
    totalItems
  } = state;
  return totalItems < slidesToShow;
}

/**
 * Calculates the initial state properties for the carousel.
 * @param {object} state - The internal carousel state.
 * @param {boolean} state.domLoaded - True if the DOM is loaded.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @param {number} state.containerWidth - The width of the carousel container.
 * @param {number} state.itemWidth - The width of a single item.
 * @param {object} props - The carousel props.
 * @param {string} props.deviceType - The type of device (e.g., 'desktop', 'mobile').
 * @param {object} props.responsive - Responsive settings for different device types.
 * @param {boolean} props.ssr - Server-side rendering flag.
 * @param {boolean} props.partialVisbile - Old prop for partial visibility.
 * @param {boolean} props.partialVisible - New prop for partial visibility.
 * @returns {{
 * shouldRenderOnSSR: boolean,
 * flexBisis: number | string | undefined,
 * domFullyLoaded: boolean,
 * partialVisibilityGutter: number | undefined,
 * shouldRenderAtAll: boolean
 * }} The initial state properties.
 */
function getInitialState(state, props) {
  const {
    domLoaded,
    slidesToShow,
    containerWidth,
    itemWidth
  } = state;
  // old wrongly spelt partialVisbile prop kept to not make changes breaking
  const {
    deviceType,
    responsive,
    ssr,
    partialVisbile,
    partialVisible
  } = props;
  let flexBisis;
  const domFullyLoaded = Boolean(
    domLoaded && slidesToShow && containerWidth && itemWidth
  );
  if (ssr && deviceType && !domFullyLoaded) {
    flexBisis = getWidthFromDeviceType(deviceType, responsive);
  }
  const shouldRenderOnSSR = Boolean(
    ssr && deviceType && !domFullyLoaded && flexBisis
  );
  const partialVisibilityGutter = getPartialVisibilityGutter(
    responsive,
    partialVisbile || partialVisible,
    deviceType,
    state.deviceType
  );
  const shouldRenderAtAll = shouldRenderOnSSR || domFullyLoaded;
  return {
    shouldRenderOnSSR,
    flexBisis,
    domFullyLoaded,
    partialVisibilityGutter,
    shouldRenderAtAll
  };
}

/**
 * Checks if a slide at a given index is currently visible.
 * @param {number} index - The index of the slide to check.
 * @param {object} state - The internal carousel state.
 * @param {number} state.currentSlide - The index of the current active slide.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @returns {boolean} True if the slide is visible, false otherwise.
 */
function getIfSlideIsVisbile(index, state) {
  const {
    currentSlide,
    slidesToShow
  } = state;
  return index >= currentSlide && index < currentSlide + slidesToShow;
}

/**
 * Calculates the transform value for center mode.
 * @param {object} state - The internal carousel state.
 * @param {number} state.transform - The current transform value.
 * @param {number} state.currentSlide - The index of the current active slide.
 * @param {number} state.itemWidth - The width of a single item.
 * @param {object} props - The carousel props.
 * @param {boolean} props.infinite - True if infinite scrolling is enabled.
 * @param {number} [transformPlaceHolder] - An optional placeholder transform value.
 * @returns {number} The calculated transform value.
 */
function getTransformForCenterMode(state, props, transformPlaceHolder) {
  const transform = transformPlaceHolder || state.transform;
  if (
    (!props.infinite && state.currentSlide === 0) ||
    notEnoughChildren(state)
  ) {
    return transform;
  } else {
    return transform + state.itemWidth / 2;
  }
}

/**
 * Checks if the carousel is at its left end.
 * @param {object} state - The internal carousel state.
 * @param {number} state.currentSlide - The index of the current active slide.
 * @returns {boolean} True if at the left end, false otherwise.
 */
function isInLeftEnd({
  currentSlide
}) {
  return !(currentSlide > 0);
}

/**
 * Checks if the carousel is at its right end.
 * @param {object} state - The internal carousel state.
 * @param {number} state.currentSlide - The index of the current active slide.
 * @param {number} state.totalItems - The total number of items.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @returns {boolean} True if at the right end, false otherwise.
 */
function isInRightEnd({
  currentSlide,
  totalItems,
  slidesToShow
}) {
  return !(currentSlide + slidesToShow < totalItems);
}

/**
 * Calculates the transform value for partial visibility mode.
 * @param {object} state - The internal carousel state.
 * @param {number} state.currentSlide - The index of the current active slide.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @param {number} state.containerWidth - The width of the carousel container.
 * @param {number} state.itemWidth - The width of a single item.
 * @param {number} [partialVisibilityGutter=0] - The gutter size for partial visibility.
 * @param {object} props - The carousel props.
 * @param {boolean} props.infinite - True if infinite scrolling is enabled.
 * @param {number} [transformPlaceHolder] - An optional placeholder transform value.
 * @returns {number} The calculated transform value.
 */
function getTransformForPartialVsibile(
  state,
  partialVisibilityGutter = 0,
  props,
  transformPlaceHolder
) {
  const {
    currentSlide,
    slidesToShow
  } = state;
  const isRightEndReach = isInRightEnd(state);
  const shouldRemoveRightGutter = !props.infinite && isRightEndReach;
  const baseTransform = transformPlaceHolder || state.transform;
  if (notEnoughChildren(state)) {
    return baseTransform;
  }
  const transform = baseTransform + currentSlide * partialVisibilityGutter;
  if (shouldRemoveRightGutter) {
    const remainingWidth =
      state.containerWidth -
      (state.itemWidth - partialVisibilityGutter) * slidesToShow;
    return transform + remainingWidth;
  }
  return transform;
}

/**
 * Parses the position based on the RTL (right-to-left) property.
 * @param {object} props - The carousel props.
 * @param {boolean} props.rtl - True if RTL mode is enabled.
 * @param {number} position - The position value.
 * @returns {number} The parsed position.
 */
export function parsePosition(props, position) {
  return props.rtl ? -1 * position : position;
}

/**
 * Calculates the overall transform value for the carousel.
 * @param {object} state - The internal carousel state.
 * @param {object} props - The carousel props.
 * @param {boolean} props.partialVisbile - Old prop for partial visibility.
 * @param {boolean} props.partialVisible - New prop for partial visibility.
 * @param {object} props.responsive - Responsive settings for different device types.
 * @param {string} props.deviceType - The type of device (e.g., 'desktop', 'mobile').
 * @param {boolean} props.centerMode - True if center mode is enabled.
 * @param {number} [transformPlaceHolder] - An optional placeholder transform value.
 * @returns {number} The final calculated transform value.
 */
function getTransform(state, props, transformPlaceHolder) {
  // old wrongly spelt partialVisbile prop kept to not make changes breaking
  const {
    partialVisbile,
    partialVisible,
    responsive,
    deviceType,
    centerMode
  } = props;
  const transform = transformPlaceHolder || state.transform;
  const partialVisibilityGutter = getPartialVisibilityGutter(
    responsive,
    partialVisbile || partialVisible,
    deviceType,
    state.deviceType
  );
  const currentTransform =
    partialVisible || partialVisbile ?
      getTransformForPartialVsibile(
        state,
        partialVisibilityGutter,
        props,
        transformPlaceHolder
      ) :
      centerMode ?
        getTransformForCenterMode(state, props, transformPlaceHolder) :
        transform;
  return parsePosition(props, currentTransform);
}

/**
 * Calculates the number of slides to slide at a time.
 * @param {object} state - The internal carousel state.
 * @param {boolean} state.domLoaded - True if the DOM is loaded.
 * @param {number} state.slidesToShow - The number of slides to show.
 * @param {number} state.containerWidth - The width of the carousel container.
 * @param {number} state.itemWidth - The width of a single item.
 * @param {object} props - The carousel props.
 * @param {number} props.slidesToSlide - The number of slides to scroll by default.
 * @param {string} props.deviceType - The type of device (e.g., 'desktop', 'mobile').
 * @param {object} props.responsive - Responsive settings for different device types.
 * @param {boolean} props.ssr - Server-side rendering flag.
 * @returns {number} The number of slides to slide.
 */
function getSlidesToSlide(state, props) {
  const {
    domLoaded,
    slidesToShow,
    containerWidth,
    itemWidth
  } = state;
  const {
    deviceType,
    responsive
  } = props;
  let slidesToScroll = props.slidesToSlide || 1;
  const domFullyLoaded = Boolean(
    domLoaded && slidesToShow && containerWidth && itemWidth
  );
  const ssr = props.ssr && props.deviceType && !domFullyLoaded;
  if (ssr) {
    Object.keys(responsive).forEach(device => {
      const {
        slidesToSlide
      } = responsive[device];
      if (deviceType === device && slidesToSlide) {
        slidesToScroll = slidesToSlide;
      }
    });
  }
  if (domFullyLoaded) {
    Object.keys(responsive).forEach(item => {
      const {
        breakpoint,
        slidesToSlide
      } = responsive[item];
      const {
        max,
        min
      } = breakpoint;
      if (
        slidesToSlide &&
        window.innerWidth >= min &&
        window.innerWidth <= max
      ) {
        slidesToScroll = slidesToSlide;
      }
    });
  }
  return slidesToScroll;
}

export {
  isInLeftEnd,
  isInRightEnd,
  getInitialState,
  getIfSlideIsVisbile,
  getTransformForCenterMode,
  getTransformForPartialVsibile,
  notEnoughChildren,
  getSlidesToSlide,
  getTransform
};